import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import { set } from 'date-fns/set';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { useViewType, ViewType } from '@eika/hooks';

import { useConfig } from '../../defaultConfig';
import { usePaymentItemDetails } from '../../fetchHooks/usePaymentItems';
import type { Account } from '../../generated-types';
import { usePaymentAccountContext } from '../../hooks/useAccountState';
import { useActorReceiver } from '../../hooks/useSetReceiver';
import useTranslation from '../../hooks/useTranslation';
import { updatePayment } from '../../services/paymentService';
import { useSigningContext } from '../../signingContext/signingContext';
import { FormattedFuturePayment } from '../../utils/mapper/futurepaymentsUtil';
import { TransactionForm, TransactionSubmitWrapper } from '../TransactionForm';
import { StopPaymentButton } from '../shared/StopPaymentButton';
import EFakturaFields from '../transaction/EFaktura/EFakturaFields';
import { PaymentActionType } from '../transaction/EFaktura/form/actions';
import paymentReducer from '../transaction/EFaktura/form/reducer';
import { EfakturaState, getFutureState } from '../transaction/EFaktura/form/state';
import useFormContext, { EFakturaFormContext } from '../transaction/EFaktura/form/useFormContext';
import useValidate from '../transaction/EFaktura/form/validate';
import FuturePaymentButtonWrapper from './FuturePaymentButtonWrapper';
import FuturePaymentSigningWrapper from './FuturePaymentSigningWrapper';
import FuturePaymentTitle from './FuturePaymentTitle';
import FuturePaymentWarningMessage from './FuturePaymentWarningMessage';
import mapUpdateEfakturaRequest from './utils/mapUpdateEfakturaRequest';

interface Props {
    futurePayment: FormattedFuturePayment;
    onClose: () => void;
    hidePaymentButton: { value: boolean; set: (value: boolean) => void };
    account?: Account;
}

const timeReset = { hours: 0, minutes: 0, seconds: 0, milliseconds: 0 };

const FuturePaymentDetailsEFaktura: React.FC<Props> = ({ futurePayment, onClose, hidePaymentButton, account }) => {
    const translated = useTranslation();

    const { featureToggles } = useConfig();
    const [status, setStatus] = useState<'updating' | 'payNow' | null>(null);

    const { handleChange, checkFormValidity, state } = useFormContext();
    const { targetFromAccount } = usePaymentAccountContext();

    const doUpdateEfaktura = useCallback(
        (efakturaState: EfakturaState) => {
            return updatePayment({ value: futurePayment.id }, mapUpdateEfakturaRequest(efakturaState));
        },
        [futurePayment.id],
    );

    const { signingOperationPending, startSigning } = useSigningContext(doUpdateEfaktura);

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if ((await checkFormValidity()) && account) {
            setStatus('updating');
            startSigning(state);
        }
    };

    const onPayNow = useCallback(() => {
        handleChange({ type: PaymentActionType.PAYMENT_DATE, payload: getZonedDate() });
        if (account) {
            setStatus('payNow');
            startSigning({ ...state, paymentDate: getZonedDate() });
        }
    }, [account, startSigning, handleChange, state]);

    useEffect(() => {
        if (futurePayment.fromAccount.accountNumber) {
            handleChange({
                type: PaymentActionType.FROM_ACCOUNT_SELECTED,
                payload: futurePayment.fromAccount.accountNumber,
            });
        }
    }, [futurePayment.fromAccount.accountNumber, handleChange]);

    const receiver = useActorReceiver(futurePayment.toAccount);
    useEffect(() => {
        if (receiver) {
            handleChange({
                type: PaymentActionType.TO_ACCOUNT_SELECTED,
                payload: receiver,
            });
        }
    }, [handleChange, receiver]);

    const { isLoading: detailsLoading, data: futurePaymentDetails } = usePaymentItemDetails(
        { value: futurePayment.id },
        {
            onSuccess: (futurePaymentDetails) => {
                handleChange({
                    type: PaymentActionType.SET_INITIAL_KID_AND_NOTE,
                    payload: {
                        note: futurePaymentDetails.note,
                        kid: futurePaymentDetails.kid,
                    },
                });
                if (futurePaymentDetails?.eFakturaId) {
                    handleChange({
                        type: PaymentActionType.SET_EFAKTURA_ID,
                        payload: futurePaymentDetails.eFakturaId,
                    });
                }
            },
        },
    );

    const hasDateChange = useMemo(() => {
        const futurePaymentDate = getZonedDate(set(futurePayment.date, timeReset));
        const statePaymentDate = getZonedDate(set(state.paymentDate ?? new Date(), timeReset));
        const todayReset = getZonedDate(set(new Date(), timeReset));
        const notTheSameAsState = !isSameDay(futurePaymentDate, statePaymentDate);
        const inThePast = isBefore(futurePaymentDate, todayReset);

        if (inThePast && notTheSameAsState) {
            return isAfter(statePaymentDate, todayReset);
        }

        return notTheSameAsState;
    }, [futurePayment.date, state.paymentDate]);

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;

    const { canReject, canEdit: editAllowed } = futurePayment.permissions;
    const canEdit = editAllowed && featureToggles.enableUpdateFutureEfaktura;

    const hasChange =
        -futurePayment.amount !== state.amount ||
        futurePayment.fromAccount?.accountNumber?.value !== state.fromAccount?.value ||
        (futurePaymentDetails?.ownText ?? '') !== state.note ||
        hasDateChange;

    return (
        <FuturePaymentSigningWrapper
            onClose={onClose}
            paymentDate={state.paymentDate}
            fromAccountId={targetFromAccount?.accountId}
            paymentType={futurePayment.paymentType}
            status={status}
        >
            <FuturePaymentTitle
                useLabel
                futurePaymentType={futurePayment.paymentType}
                eFaktura={{
                    id: futurePaymentDetails?.eFakturaId,
                    loading: detailsLoading,
                    show: !isMobile,
                }}
            />
            <TransactionForm className="future-payment-details__form" onSubmit={onSubmit}>
                <EFakturaFields
                    kidLoading={detailsLoading}
                    locked={!canEdit || signingOperationPending}
                    eFakturaId={futurePaymentDetails?.eFakturaId}
                    loadingEfakturaId={detailsLoading}
                />
                <TransactionSubmitWrapper className="future-payment__button-row">
                    <FuturePaymentButtonWrapper>
                        <FuturePaymentButtonWrapper.FirstRow>
                            {!hidePaymentButton.value && (
                                <Button buttonType="negative" onClick={onClose}>
                                    {translated.cancel}
                                </Button>
                            )}
                            {canEdit && hasChange && (
                                <ButtonWithSpinner
                                    className="future-payment-details__save-btn"
                                    buttonType="positive"
                                    type="submit"
                                    showSpinner={signingOperationPending}
                                >
                                    {translated.recurringPayments.update}
                                </ButtonWithSpinner>
                            )}
                            {canEdit &&
                                !hasChange &&
                                featureToggles.enablePayNow &&
                                !signingOperationPending &&
                                !hidePaymentButton.value && (
                                    <ButtonWithSpinner
                                        className="future-payment-details__pay-now-btn"
                                        buttonType="positive"
                                        showSpinner={signingOperationPending}
                                        onClick={onPayNow}
                                    >
                                        {translated.payNow}
                                    </ButtonWithSpinner>
                                )}
                        </FuturePaymentButtonWrapper.FirstRow>
                        {canReject && !hasChange && (
                            <StopPaymentButton
                                onExpandDialog={hidePaymentButton.set}
                                paymentId={futurePayment.id}
                                paymentType={futurePayment.paymentType}
                                setStatusToStopping={() => setStatus(null)}
                            />
                        )}
                    </FuturePaymentButtonWrapper>
                </TransactionSubmitWrapper>
            </TransactionForm>
            {!canEdit && <FuturePaymentWarningMessage paymentType={futurePayment.paymentType} canReject={canReject} />}
        </FuturePaymentSigningWrapper>
    );
};

export default (props: Props) => (
    <EFakturaFormContext
        validateHook={useValidate}
        reducer={paymentReducer}
        initialState={getFutureState(props.futurePayment)}
    >
        <FuturePaymentDetailsEFaktura {...props} />
    </EFakturaFormContext>
);
